import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileLock2 } from "lucide-react";

const STATUSES = ["open", "in_progress", "resolved", "rejected"];
const color = (s) => ({ open: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700", resolved: "bg-emerald-100 text-emerald-700", rejected: "bg-slate-100 text-slate-600" }[s] || "bg-slate-100");

export const PrivacyRequestsCard = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/privacy-requests").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/privacy-requests/${id}`, { status });
      setRows((rs) => rs.map((r) => (r.request_id === id ? data : r)));
      toast.success("Request updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="privacy-requests-card">
      <div className="flex items-center gap-2"><FileLock2 className="h-4 w-4 text-[#2563EB]" /><h3 className="text-sm font-semibold text-slate-900">Data Subject Requests</h3><Badge variant="outline" className="text-[10px]">{rows.length}</Badge></div>
      <p className="text-xs text-slate-500 mt-1">Access / rectification / deletion / export requests from workspace users. Handle manually and track status here.</p>
      <div className="mt-4 space-y-2">
        {rows.length === 0 && <p className="text-sm text-slate-400">No requests.</p>}
        {rows.map((r) => (
          <div key={r.request_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`privacy-request-${r.request_id}`}>
            <Badge className="capitalize shrink-0">{r.type}</Badge>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-800 truncate">{r.user_name} <span className="text-slate-400 text-xs">· {r.user_email}</span></div>
              {r.note && <div className="text-xs text-slate-500 truncate">{r.note}</div>}
            </div>
            <Badge className={`capitalize shrink-0 ${color(r.status)}`}>{r.status.replace(/_/g, " ")}</Badge>
            <Select value={r.status} onValueChange={(v) => setStatus(r.request_id, v)}>
              <SelectTrigger className="h-8 w-[130px] text-xs shrink-0" data-testid={`privacy-request-status-${r.request_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </Card>
  );
};
